import React, { useState, useEffect, useCallback } from 'react';
import { getDashboardData } from '../../services/adminApi';
import StatCard from '../../components/admin/analytics/StatCard';
import SalesChart from '../../components/admin/analytics/SalesChart';
import TopProductsTable from '../../components/admin/analytics/TopProductsTable';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { subMonths, subDays } from 'date-fns';

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
  }).format(amount);
};

const AnalyticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    startDate: subMonths(new Date(), 3),
    endDate: new Date(),
  });

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {
        startDate: dateRange.startDate.toISOString(),
        endDate: dateRange.endDate.toISOString(),
      };
      const response = await getDashboardData(params);
      if (response.success) {
        setData(response.dashboard);
      }
    } catch (error) {
      console.error("Failed to fetch dashboard data", error);
    } finally {
      setLoading(false);
    }
  }, [dateRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDateRangeChange = (range) => {
    setDateRange(range);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-semibold">Analytics</h1>
      </div>

      {loading ? (
        <p>Loading analytics...</p>
      ) : data ? (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard title="Total Revenue" value={formatCurrency(data.totalRevenue)} />
            <StatCard title="Total Orders" value={data.totalOrders} />
            <StatCard title="New Customers" value={data.totalCustomers} />
            <StatCard title="Total Products" value={data.totalProducts} />
          </div>

          <Card>
            <CardHeader className="flex flex-row justify-between items-center">
              <CardTitle>Sales Trends</CardTitle>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => handleDateRangeChange({ startDate: subMonths(new Date(), 3), endDate: new Date() })}>Last 3 months</Button>
                <Button variant="ghost" onClick={() => handleDateRangeChange({ startDate: subDays(new Date(), 30), endDate: new Date() })}>Last 30 days</Button>
                <Button variant="ghost" onClick={() => handleDateRangeChange({ startDate: subDays(new Date(), 7), endDate: new Date() })}>Last 7 days</Button>
              </div>
            </CardHeader>
            <CardContent>
              <SalesChart data={data.orders || []} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Top Selling Products</CardTitle>
            </CardHeader>
            <CardContent>
              <TopProductsTable data={data.topSellingProducts} />
            </CardContent>
          </Card>
        </div>
      ) : (
        <p>No analytics data available for the selected date range.</p>
      )}
    </div>
  );
};

export default AnalyticsPage;
